import type { RecommendationPage } from '@ederest/contracts';
import { Card } from '../../shared/ui/Card.js';
import { FrictionScore } from '../../shared/ui/FrictionScore.js';
import { ReadingList, type ReadingRow } from '../../shared/ui/ReadingList.js';
import { isolate, targetOf } from './recommendation-reading.js';

const count = (value: number) => value.toLocaleString('fr-FR');
const plural = (value: number, singular: string, pluralForm: string) => (value > 1 ? pluralForm : singular);

/**
 * The queue read in plain words: how many decisions wait, which one to open
 * first and why, and what a decision does. Built from the page the API
 * returned; it counts nothing the API did not send.
 */
export function RecommendationSummary({ page }: { page: RecommendationPage | undefined }) {
  if (!page) return null;
  const pending = page.items.filter((item) => item.state === 'pending');
  const first = pending[0];
  // The split by kind is only true when the whole queue is on this page.
  const complete = page.items.length === page.totalCount;
  const siteChanges = pending.filter((item) => item.kind === 'platform_change').length;
  const helps = pending.length - siteChanges;

  const rows: ReadingRow[] = [
    {
      label: 'À décider',
      content:
        page.pendingCount === 0 ? (
          'Aucune recommandation en attente : la file est à jour.'
        ) : (
          <>
            <strong>{count(page.pendingCount)}</strong>{' '}
            {plural(page.pendingCount, 'proposition attend', 'propositions attendent')} une décision de
            l'équipe.
            {complete && pending.length > 0 && (
              <span className="reading-list__sub">
                {count(helps)} {plural(helps, 'aide à afficher', 'aides à afficher')} sur le portail
                {siteChanges > 0 &&
                  ` · ${count(siteChanges)} ${plural(siteChanges, 'modification', 'modifications')} du site à demander`}
              </span>
            )}
          </>
        ),
    },
  ];

  if (first) {
    rows.push({
      label: 'À traiter en premier',
      content: (
        <>
          {isolate(first.journeyName)} › {isolate(first.stepName)}
          <span className="reading-list__sub">
            {targetOf(first)} · {count(first.evidence.affectedSessions)}{' '}
            {plural(first.evidence.affectedSessions, 'usager concerné', 'usagers concernés')}
          </span>
        </>
      ),
      visual: <FrictionScore score={first.evidence.frictionScore} />,
      action: { label: 'Voir le détail', to: `/recommendations?selected=${first.id}` },
    });
  }

  rows.push({
    label: 'Comment décider',
    content: (
      <>
        Ouvrez une ligne : les chiffres qui la justifient et l'aide proposée s'affichent.
        <span className="reading-list__sub">
          Acceptez, ajustez ou écartez avec un motif. Rien n'apparaît sur le portail sans validation puis
          publication par l'équipe.
        </span>
      </>
    ),
  });

  return (
    <div data-testid="recommendation-summary">
      <Card title="En résumé" className="queue__summary">
        <ReadingList rows={rows} />
      </Card>
    </div>
  );
}
